import type { InspectionRecordAttachment } from '@/services/inspections/types';
import { s3Service } from '@/services/s3';
import { extractOriginalName } from '@/shared/utils/extract-original-name.util';
import { downloadFromSignedUrl } from '@/shared/utils/signed-url.util';
import { Dialog } from '@noorden-insurtech/alba-design-system';
import { Download, FileText, Folder, Loader2 } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useRecordsInspection } from '../hooks/use-records-inspection.hook';

interface InspectionAttachmentsModalProps {
  isOpen: boolean;
  onClose: () => void;
  policyId?: string | null;
  inspectionId?: string | null;
}

export function InspectionAttachmentsModal({
  isOpen,
  onClose,
  policyId,
  inspectionId,
}: InspectionAttachmentsModalProps) {
  const hasIds = Boolean(policyId?.trim() && inspectionId?.trim());
  const [downloadingId, setDownloadingId] = useState<string | null>(null);

  const { data: records, isLoading } = useRecordsInspection(
    policyId,
    inspectionId,
    { enabled: isOpen && hasIds }
  );

  const attachments = useMemo<InspectionRecordAttachment[]>(() => {
    if (!records?.length) return [];

    const seen = new Set<string>();
    const list: InspectionRecordAttachment[] = [];

    for (const record of records) {
      for (const attachment of record.attachments ?? []) {
        if (!attachment?.id || seen.has(attachment.id)) continue;
        seen.add(attachment.id);
        list.push(attachment);
      }
    }

    return list;
  }, [records]);

  async function handleDownloadAttachment(
    attachment: InspectionRecordAttachment
  ) {
    try {
      setDownloadingId(attachment.id);
      const signedUrl = await s3Service.fetchPresignedDownloadUrl(
        attachment.keyPath
      );
      await downloadFromSignedUrl(
        signedUrl,
        extractOriginalName(attachment.keyPath)
      );
    } catch (error) {
      console.error('Erro ao baixar anexo:', error);
    } finally {
      setDownloadingId(null);
    }
  }

  return (
    <Dialog.Root open={isOpen} onOpenChange={() => onClose()}>
      <Dialog.Backdrop />
      <Dialog.Positioner>
        <Dialog.Content className='w-full max-w-md pb-2'>
          <Dialog.Header className='flex flex-row items-center justify-between'>
            <Dialog.Title className='text-lg'>
              <Folder className='text-alba-gray-600 size-6' />
              Arquivos da Inspeção
            </Dialog.Title>
            <Dialog.CloseButton onClick={() => onClose()} />
          </Dialog.Header>

          <Dialog.Body className='flex flex-col gap-4 max-h-[60vh] overflow-y-auto'>
            {!hasIds ? (
              <div className='py-6 text-center text-alba-gray-600'>
                Nenhuma inspeção disponível para esta apólice.
              </div>
            ) : isLoading ? (
              <div className='py-6 text-center text-alba-gray-600'>
                Carregando arquivos da inspeção...
              </div>
            ) : attachments.length === 0 ? (
              <div className='py-6 text-center text-alba-gray-600'>
                Nenhum arquivo anexado a esta inspeção.
              </div>
            ) : (
              attachments.map(attachment => (
                <button
                  key={attachment.id}
                  type='button'
                  onClick={() => handleDownloadAttachment(attachment)}
                  disabled={downloadingId === attachment.id}
                  className='flex flex-row items-center justify-start gap-2 px-3 py-2 border border-alba-gray-400 rounded-lg hover:bg-alba-gray-50 transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed'
                >
                  <FileText className='text-alba-black-500 size-6 shrink-0' />

                  <div className='flex-1 flex flex-col items-start justify-start'>
                    <p className='font-montserrat text-sm text-alba-black-500'>
                      {extractOriginalName(attachment.keyPath)}
                    </p>
                    <p className='font-montserrat text-xs font-normal text-alba-gray-600'>
                      {attachment.documentType}
                    </p>
                  </div>

                  {downloadingId === attachment.id ? (
                    <Loader2 className='text-alba-blue-600 size-4 animate-spin' />
                  ) : (
                    <Download className='text-alba-blue-600 size-4' />
                  )}
                </button>
              ))
            )}
          </Dialog.Body>
        </Dialog.Content>
      </Dialog.Positioner>
    </Dialog.Root>
  );
}
